import { Log, User, UserManager } from 'oidc-client-ts';
import React, { useEffect, useState } from 'react';
import UserIdentityService from './UserIdentityService';
import UserManagerService from './UserManagerService';
import InfoMessageService from './InfoMessageService';
import TranslationService from './TranslationService';

const AuthenticationContext = React.createContext<Partial<AuthenticationContextProps>>({});

type AuthenticationContextProps = {
  userManager: UserManager,
  user: User | null,
  isAuthenticated: boolean | null,
  signinRedirectCallback: () => void,
  silentRefreshCallback: () => void,
  logout: () => void
}

export const AuthenticationProvider = (props: {
  children?: JSX.Element[] | JSX.Element | string | any
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const userManager = UserManagerService;

  const getUser = async () => {
    if (user) {
      return;
    }
    const loggedInUser = await userManager.getUser();

    if (loggedInUser) {
      setUser(loggedInUser);
      UserIdentityService.setUserDataInLocalStorage(loggedInUser);
    }

    setIsLoading(false);
  }

  const signinRedirectCallback = () => {
    userManager.signinRedirectCallback().then((user: User) => {
      UserIdentityService.setUserDataInLocalStorage(user);
      window.location.href = "/";
    }).catch(err => {
      InfoMessageService.error(TranslationService.translate('GeneralErrorOccured'));
    });
  };

  const silentRefreshCallback = () => {
    userManager.signinSilentCallback()
      .catch((err) => console.error(err));
  };

  const logout = async () => {
    await userManager.signoutRedirect();
    await userManager.removeUser();

    UserIdentityService.clearUserDataFromLocalAndSessionStorage();
  }

  useEffect(() => {
    if (user) {
      setIsAuthenticated(!!user.access_token);
    } else {
      if (!isLoading) {
        setIsAuthenticated(false);
      }
    }

  }, [user, isLoading])


  useEffect(() => {
    Log.setLogger(console);
    Log.setLevel(Log.WARN);

    userManager.events.addAccessTokenExpiring(accessTokenExpiring);
    userManager.events.addAccessTokenExpired(accessTokenExpired);
    userManager.events.addSilentRenewError(silentRenewError);
    userManager.events.addUserLoaded(userLoaded);

    getUser();

    return () => {
      userManager.events.removeAccessTokenExpiring(accessTokenExpiring);
      userManager.events.removeAccessTokenExpired(accessTokenExpired);
      userManager.events.addSilentRenewError(silentRenewError);
      userManager.events.addUserLoaded(userLoaded);
    }

    // eslint-disable-next-line
  }, []);

  const userLoaded = (user: User) => {
    setUser(user);
  };

  const accessTokenExpired = () => {
    console.debug('accessTokenExpired');
  };

  const accessTokenExpiring = () => {
    console.debug('accessTokenExpiring');
  };

  const silentRenewError = () => {
    console.debug('silentRenewError');
  };

  return (
    <AuthenticationContext.Provider value={{ userManager, user, isAuthenticated, signinRedirectCallback, silentRefreshCallback, logout }}>
      {!isLoading ? React.Children.only(props.children) : ''}
    </AuthenticationContext.Provider>
  )
};

export const AuthenticationConsumer = AuthenticationContext.Consumer;

export default AuthenticationProvider
